import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { BarFiltersProps, BarSortingFields, DisabledBarVenueFieldsProps, ListBarModel, Order } from '../models';

interface IBarsContext {
  barsUpdate: boolean;
  toggleBarsUpdate: () => void;
  toggleBarDetailsUpdate: () => void;
  bars: ListBarModel[];
  setBars: (val: ListBarModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: BarSortingFields;
  setOrderBy: (val: BarSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: BarSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  setProcessValues: (val: boolean) => void;
  processValues: boolean;
  barDetailsRefresh: boolean;
  setDisabledVenuesFields: (val: DisabledBarVenueFieldsProps) => void;
  resetCreateBarVenuesFields: () => void;
  disabledVenuesFields: DisabledBarVenueFieldsProps;
  setFilters: (val: BarFiltersProps) => void;
  filters: BarFiltersProps;
  resetFilters: () => void;
}

export const BarsContext = createContext<IBarsContext>({} as IBarsContext);

export const BarsProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [barsUpdate, setBarsUpdate] = useState(false);
  const toggleBarUpdate = (): void => setBarsUpdate(!barsUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<BarSortingFields>(toggleBarUpdate, 'name', 'asc');

  const [bars, setBars] = useState<ListBarModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [processValues, setProcessValues] = useState(false);
  const emptyFilters = {
    clientName: undefined,
    venueName: undefined,
    barType: undefined,
    statusType: undefined
  };
  const [filters, setFilters] = useState<BarFiltersProps>(emptyFilters);
  const resetFilters = (): void => {
    setFilters(emptyFilters);
    setPage(0);
    setLimit(20);
  };
  const initialBarVenuesFields: DisabledBarVenueFieldsProps = {
    accessDays: false,
    productDeliveryDays: false,
    accessWindowOpen: false,
    accessWindowClose: false
  };
  const [disabledVenuesFields, setDisabledVenuesFields] = useState<DisabledBarVenueFieldsProps>(initialBarVenuesFields);
  const [barDetailsRefresh, setBarDetailsRefresh] = useState(true);
  const toggleBarDetailsUpdate = (): void => setBarDetailsRefresh(!barDetailsRefresh);
  const resetCreateBarVenuesFields = (): void => setDisabledVenuesFields(initialBarVenuesFields);

  return (
    <BarsContext.Provider
      value={{
        filters,
        setFilters,
        resetFilters,
        barsUpdate,
        barDetailsRefresh,
        toggleBarsUpdate: toggleBarUpdate,
        toggleBarDetailsUpdate,
        resetCreateBarVenuesFields,
        bars,
        processValues,
        setProcessValues,
        setDisabledVenuesFields,
        disabledVenuesFields,
        setBars,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading
      }}
    >
      {children}
    </BarsContext.Provider>
  );
};
